import {Component, Inject, OnInit} from "@angular/core";
import * as moment from "moment/moment";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {GlobalPreferences} from "../../../../../core/helpers/global-data";
import {AccountControllerService} from "../../../../../core/api/controllers/account-controller/account-controller.service";
import {AdminControllerService} from "../../../../../core/api/controllers/admin/admin-controller.service";
import {SettingsService} from "../../../../../core/services/settings.service";
import {MatSnackBar} from "@angular/material/snack-bar";
import {IOrganization} from "../../../../../shared/interfaces/IOrganization";
import {NotificationService} from "../../../../../core/services/notification.service";

@Component({
  selector: "app-organization-usage-report-dialog",
  templateUrl: "./organization-usage-report-dialog.component.html",
  styleUrls: ["./organization-usage-report-dialog.component.scss"],
})
export class OrganizationUsageReportDialogComponent implements OnInit {
  newStartDate = undefined;
  newEndDate = undefined;
  isLoading = false;
  organizations: IOrganization[] = [];
  selectedOrganizationId: string = null;
  organizationsLoading = false;

  constructor(
    private accountController: AccountControllerService,
    private adminController: AdminControllerService,
    public global: GlobalPreferences,
    @Inject(MAT_DIALOG_DATA)
    private data: any,
    private dialogRef: MatDialogRef<OrganizationUsageReportDialogComponent>,
    private settingsService: SettingsService,
    private matSnackBar: MatSnackBar,
    private notificationService: NotificationService
  ) {}

  ngOnInit() {
    this.loadOrganizations();
  }

  changeDate(dateRangeStart: HTMLInputElement, dateRangeEnd: HTMLInputElement) {
    this.newStartDate = moment(dateRangeStart.value).toDate();
    this.newEndDate = moment(dateRangeEnd.value).toDate();
  }

  loadOrganizations() {
    this.organizationsLoading = true;
    this.adminController.getOrganizationsWithLocations({
      page: 0,
      itemsPerPage: 1000,
      search: ""
    }).subscribe({
      next: (response) => {
        this.organizations = response.data;
        this.organizationsLoading = false;
      },
      error: (err) => {
        this.notificationService.error("Failed to load organizations");
        this.organizationsLoading = false;
      }
    });
  }

  isValidDate(newStartDate, newEndDate) {
    return (
      moment(newStartDate).isValid() &&
      moment(newEndDate).isValid() &&
      newStartDate !== undefined &&
      newEndDate !== undefined
    );
  }

  canExport() {
    return this.isValidDate(this.newStartDate, this.newEndDate) && this.selectedOrganizationId;
  }

  getResultData() {
    if (this.canExport()) this.fetchReport();
  }

  fetchReport() {
    this.isLoading = true;
    const selectedOrg = this.organizations.find(org => org.id === this.selectedOrganizationId);
    this.accountController
      .exportOrganizationUsageReport({
        start: this.newStartDate.toISOString(),
        end: this.newEndDate.toISOString(),
        organizationId: this.selectedOrganizationId,
      })
      .subscribe({
        next: (res) => {
          this.downloadFile(res, selectedOrg?.name);
          this.dialogRef.close();
          this.isLoading = false;
        },
        error: (err) => {
          this.matSnackBar.open(err.error?.error ?? "Something went wrong", "Close", {
            duration: 3000,
          });
          if(err.error?.validationErrors) {
            this.matSnackBar.open(err.error.validationErrors[0].message, "Close", {
              duration: 3000,
            });
          }
          this.isLoading = false;
        },
      });
  }

  downloadFile(res, organizationName?: string) {
    const blob = new Blob([res], { type: "application/vnd.ms-excel" });
    const url = window.URL.createObjectURL(blob);
    // Download the file with the given filename
    const a = document.createElement("a");
    a.href = url;
    const name =
      (organizationName || "Organization") +
      "_Organization_Usage_Report_" +
      this.formatDateWithOffset() +
      ".xlsx";
    a.download = name;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  formatDateWithOffset() {
    // Get the current UTC time
    const now = new Date();

    // Compute the local GMT offset (in hours)
    const timezoneOffset = now.getTimezoneOffset() / 60; // returns in minutes, so divide by 60 to get hours
    const gmtOffset = -timezoneOffset; // Invert because getTimezoneOffset() gives negative for positive GMT zones

    // Add the GMT offset (in hours) to the UTC time
    const offsetInMilliseconds = gmtOffset * 60 * 60 * 1000;
    const adjustedTime = new Date(now.getTime() + offsetInMilliseconds);

    // Format the adjusted time to "MM-dd-yyyy_HH-mm-ss"
    const formattedDate = adjustedTime
      .toISOString()
      .replace("T", "_")
      .replace(/\..+/, "")
      .split(":")
      .join("-");
    return (
      formattedDate.substring(5, 7) +
      "-" +
      formattedDate.substring(8, 10) +
      "-" +
      formattedDate.substring(0, 4) +
      "_" +
      formattedDate.substring(11)
    );
  }
}

